import {
  GetAppRounded as DownloadIcon,
  DeleteRounded as DeleteIcon,
  VisibilityRounded as ViewIcon,
} from "@mui/icons-material";
import {
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as dates from "common/dates";
import Component from "components/Component";
import FileUpload from "components/FilesFileUpload";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import IconButton from "components/common/IconButton";
import SmallLoader from "components/common/SmallLoader";
import TextNote from "components/common/TextNote";
import Visible from "components/common/Visible";
import * as fileUtils from "fileUtils";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as filesService from "services/filesService";
import * as rulesStore from "store/rules";

const SubStack = ({ show, children }) => {
  return show ? (
    <Stack
      sx={{
        paddingLeft: "2rem !important",
        width: "100%",
      }}
      gap={1}
    >
      {children}
    </Stack>
  ) : (
    <></>
  );
};

const DownloadingIcon = () => {
  return <CircularProgress size={22} />;
};

const FileCard = ({ fileId, onDeleteFile }) => {
  const { data: meta, isLoading } = filesService.useGetFileMetadataQuery(
    fileId,
    {
      skip: !fileId,
    }
  );

  const [isDownloading, setIsDownloading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const download = async (e) => {
    e.stopPropagation();
    setIsDownloading(true);
    try {
      await fileUtils.saveFile(fileId, meta?.originalFilename);
    } catch (e) {
      setHasError(true);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={"Approval Document"}
        titleTypographyProps={{
          variant: "h6",
        }}
      />
      <CardContent>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={0.5}
        >
          {isLoading ? (
            <SmallLoader />
          ) : (
            <>
              <Typography sx={{ width: "100%" }}>
                {meta?.originalFilename ?? "[Unknown]"}
              </Typography>
            </>
          )}
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"flex-end"}
            gap={0.5}
          >
            <IconButton
              icon={ViewIcon}
              onClick={() => {
                window.open(
                  "/attachments/fileView?" + new URLSearchParams({ fileId }),
                  "_blank"
                );
              }}
            />
            <IconButton
              icon={isDownloading ? DownloadingIcon : DownloadIcon}
              onClick={download}
              isDisabled={isDownloading}
              error={hasError}
            />
            <IconButton icon={DeleteIcon} onClick={onDeleteFile} />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const SpecialAcceptance = () => {
  const dispatch = useDispatch();
  const specialAcceptance =
    useSelector(rulesStore.selectByKey("specialAcceptance")) ?? {};

  const today = () => dates.toISODateString(dates.getDate());

  const update = (updateFn) =>
    dispatch(
      rulesStore.updateByKey({
        key: "specialAcceptance",
        update: updateFn,
      })
    );

  const updateStage = (stage) => (values) =>
    update((state) => ({
      ...state,
      [stage]: {
        ...state?.[stage],
        ...values,
      },
    }));

  const updateStageValue = (stage) => (value) =>
    updateStage(stage)({
      value,
      date: today(),
    });

  const updateStageDate = (stage) => (date) => updateStage(stage)({ date });

  return (
    <Component title={"Special Acceptance"}>
      <Stack gap={2}>
        <Stack alignItems={"flex-start"}>
          <FormControlLabel
            control={
              <Checkbox
                checked={specialAcceptance.required?.value ?? false}
                onChange={(e) => updateStageValue("required")(e.target.checked)}
                color={"primary"}
              />
            }
            label={<Typography>{"Required"}</Typography>}
          />
          <SubStack show={specialAcceptance.required?.value}>
            <TextField
              label={"Reason"}
              value={specialAcceptance.reason?.value ?? ""}
              onChange={(e) => updateStageValue("reason")(e.target.value)}
              variant={"filled"}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </SubStack>
        </Stack>

        {specialAcceptance.required?.value && (
          <>
            <Stack alignItems={"flex-start"}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={specialAcceptance.requested?.value ?? false}
                    onChange={(e) =>
                      updateStageValue("requested")(e.target.checked)
                    }
                    color={"primary"}
                  />
                }
                label={<Typography>{"Requested"}</Typography>}
              />
              <SubStack show={specialAcceptance.requested?.value}>
                <FormattedDatePicker
                  compact
                  label={"Date"}
                  value={specialAcceptance.requested?.date ?? ""}
                  onChangeDate={updateStageDate("requested")}
                />
              </SubStack>
            </Stack>

            {specialAcceptance.requested?.value && (
              <Stack alignItems={"flex-start"}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={specialAcceptance.received?.value ?? false}
                      onChange={(e) =>
                        updateStageValue("received")(e.target.checked)
                      }
                      color={"primary"}
                    />
                  }
                  label={<Typography>{"Received"}</Typography>}
                />
                <SubStack show={specialAcceptance.received?.value}>
                  <FormattedDatePicker
                    compact
                    label={"Date"}
                    value={specialAcceptance.received?.date ?? ""}
                    onChangeDate={updateStageDate("received")}
                  />
                </SubStack>

                <SubStack show={specialAcceptance.received?.value}>
                  <FormControl sx={{ marginTop: "0.75rem" }}>
                    <RadioGroup
                      value={
                        specialAcceptance.approved?.value == null
                          ? ""
                          : specialAcceptance.approved?.value
                          ? "approved"
                          : "declined"
                      }
                      onChange={(e) =>
                        updateStageValue("approved")(
                          e.target.value === "approved"
                        )
                      }
                    >
                      <FormControlLabel
                        value={"approved"}
                        control={<Radio />}
                        label={"Approved"}
                      />
                      <FormControlLabel
                        value={"declined"}
                        control={<Radio />}
                        label={"Declined"}
                      />
                    </RadioGroup>
                  </FormControl>
                  {specialAcceptance.received?.fileId && (
                    <FileCard
                      fileId={specialAcceptance.received?.fileId}
                      onDeleteFile={() =>
                        updateStage("received")({ fileId: null })
                      }
                    />
                  )}
                  {specialAcceptance.approved?.value &&
                    specialAcceptance.received?.fileId == null && (
                      <FileUpload
                        onDrop={(fileId) => updateStage("received")({ fileId })}
                        asAtDateNotRequired={true}
                        title={"Upload Approval Document"}
                        dropMessage={
                          "Drop approval document here or click to select file"
                        }
                      />
                    )}
                </SubStack>
              </Stack>
            )}

            <TextNote noteKey={"specialAcceptance"} title={"Note"} />
          </>
        )}

        <Visible
          byTag={"<SpecialAcceptance>.riCessionException"}
          defaultVisible={false}
        >
          <Stack alignItems={"flex-start"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={specialAcceptance.riCessionException?.value ?? false}
                  onChange={(e) =>
                    updateStageValue("riCessionException")(e.target.checked)
                  }
                  color={"primary"}
                />
              }
              label={<Typography>{"RI Cession Exception"}</Typography>}
            />
            <SubStack show={specialAcceptance?.riCessionException?.value}>
              <TextField
                label={"Note"}
                value={specialAcceptance.riCessionException?.note ?? ""}
                onChange={(e) =>
                  updateStage("riCessionException")({ note: e.target.value })
                }
                variant={"filled"}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </SubStack>
          </Stack>
        </Visible>
      </Stack>
    </Component>
  );
};

export default SpecialAcceptance;
